/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { PDFBM } from "../components/PDFBM";
import { usePutFarmerBankStatus } from "../components/api/mutations/hooks";
import { useSnakBarTran } from "../hook";
import { getImageSrc } from "../utils/getURL";

/* ================= ENUMS ================= */

const BANK_STATUS = {
    RECEIVED: "BANK_DOCUMENT_RECEIVED",
    ON_HOLD: "ON_HOLD",
    VERIFIED: "BANK_DOCUMENT_VERIFIED",
    REJECTED: "BANK_DOCUMENT_REJECTED",
};

const DOCUMENT_TYPE = {
    CHEQUE: "CANCELLED_CHEQUE",
    PASSBOOK: "BANK_PASSBOOK",
    STATEMENT: "BANK_STATEMENT",
};

/* ================= STYLES ================= */

const RootStyle = styled(Box)({
    display: "grid",
    gridTemplateColumns: "70% 30%",
    gap: 12,
    padding: 12,
    height: 520,
    background: "#f5f6f8",
    borderRadius: 12,
});

const Card = styled(Box)({
    padding: 12,
    borderRadius: 10,
    background: "#fff",
    boxShadow: "0 1px 6px rgba(0,0,0,0.12)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
});

const ImageWrapper = styled(Box)({
    flex: 1,
    borderRadius: 10,
    background: "#eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
});

const Image = styled("img")({
    width: "100%",
    height: "100%",
    objectFit: "contain",
});

/* ================= COMPONENT ================= */

export default function BankDocumentPreview({ document, onStatusChange }) {
    const [imgSrc, setImgSrc] = useState(null);
    const [loading, setLoading] = useState(false);

    const [documentType, setDocumentType] = useState(
        document?.documentType || DOCUMENT_TYPE.CHEQUE
    );
    const [currentStatus, setCurrentStatus] = useState(document?.bankStatus);

    const { mutate, isPending } = usePutFarmerBankStatus();
    const { showSuccessSnackbar, showErrorSnackbar } = useSnakBarTran();

    const bankDocumentPath = document?.bankDocument;

    const isApproved = currentStatus === BANK_STATUS.VERIFIED;
    const isRejected = currentStatus === BANK_STATUS.REJECTED;
    const isOnHold = currentStatus === BANK_STATUS.ON_HOLD;

    const canApprove =
        currentStatus === BANK_STATUS.RECEIVED || currentStatus === BANK_STATUS.ON_HOLD;

    /* ================= STATUS LABEL ================= */

    const getBankStatusLabel = (status) => {
        switch (status) {
            case BANK_STATUS.VERIFIED:
                return "Approved";
            case BANK_STATUS.REJECTED:
                return "Rejected";
            case BANK_STATUS.ON_HOLD:
                return "On Hold";
            case BANK_STATUS.RECEIVED:
                return "Received";
            default:
                return "Pending";
        }
    };

    /* ================= LOAD IMAGE ================= */

    useEffect(() => {
        const loadImage = async () => {
            if (!bankDocumentPath || bankDocumentPath.includes(".pdf")) {
                setImgSrc(null);
                return;
            }

            setLoading(true);
            try {
                const image = await getImageSrc(bankDocumentPath);
                setImgSrc(image);
            } catch {
                setImgSrc(null);
            } finally {
                setLoading(false);
            }
        };

        loadImage();
    }, [bankDocumentPath]);

    /* ================= WATCH DOCUMENT CHANGES ================= */
    useEffect(() => {
        setCurrentStatus(document?.bankStatus);
    }, [document?.bankStatus]);

    /* ================= ACTION HANDLERS ================= */

    const updateStatus = (newStatus) => {
        setCurrentStatus(newStatus);
        if (onStatusChange) onStatusChange(newStatus); // notify parent for auto-refresh
    };

    const handleApprove = () => {
        if (!canApprove) {
            showErrorSnackbar({ text: "Document is not eligible for approval" });
            return;
        }
        if (!documentType) {
            showErrorSnackbar({ text: "Please select document type" });
            return;
        }

        mutate(
            {
                farmerId: document.farmerId,
                status: BANK_STATUS.VERIFIED,
                documentType,
            },
            {
                onSuccess: () => {
                    showSuccessSnackbar({ text: "Bank details approved successfully" });
                    updateStatus(BANK_STATUS.VERIFIED);
                },
                onError: (err) =>
                    showErrorSnackbar({
                        text: err?.response?.data?.message || "Failed to approve bank details",
                    }),
            }
        );
    };

    const handleReject = () => {
        mutate(
            {
                farmerId: document.farmerId,
                status: BANK_STATUS.REJECTED,
            },
            {
                onSuccess: () => {
                    showSuccessSnackbar({ text: "Bank details rejected successfully" });
                    updateStatus(BANK_STATUS.REJECTED);
                },
                onError: (err) =>
                    showErrorSnackbar({
                        text: err?.response?.data?.message || "Failed to reject bank details",
                    }),
            }
        );
    };

    const handleOnHold = () => {
        if (isRejected) {
            showErrorSnackbar({ text: "Cannot put a rejected document on hold" });
            return;
        }

        mutate(
            {
                farmerId: document.farmerId,
                status: BANK_STATUS.ON_HOLD,
            },
            {
                onSuccess: () => {
                    showSuccessSnackbar({ text: "Bank details set to On Hold successfully" });
                    updateStatus(BANK_STATUS.ON_HOLD);
                },
                onError: (err) =>
                    showErrorSnackbar({
                        text: err?.response?.data?.message || "Failed to set bank details on hold",
                    }),
            }
        );
    };

    /* ================= PREVIEW ================= */

    const renderPreview = () => {
        if (!bankDocumentPath) return <Typography>No document uploaded</Typography>;
        if (bankDocumentPath.includes(".pdf"))
            return <PDFBM url={bankDocumentPath} farmerId={document.farmerId} showDownload />;
        if (loading) return <CircularProgress />;
        if (imgSrc) return <Image src={imgSrc} alt="Bank Document" />;
        return <Typography>No document uploaded</Typography>;
    };

    /* ================= JSX ================= */

    return (
        <RootStyle>
            {/* LEFT: PREVIEW */}
            <Card>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Document Preview
                </Typography>
                <ImageWrapper>{renderPreview()}</ImageWrapper>
            </Card>

            {/* RIGHT: INFO + ACTIONS */}
            <Box display="flex" flexDirection="column" gap={1}>
                <Card>
                    <Typography variant="subtitle2" fontWeight={650} mb={1}>
                        Document Information
                    </Typography>

                    <Typography fontSize={13}>Name: {document.name}</Typography>
                    <Typography fontSize={13}>Bank: {document.bankName}</Typography>
                    <Typography fontSize={13}>Account Number: {document.accountNumber}</Typography>
                    <Typography fontSize={13}>Bank Code: {document.bankCode}</Typography>

                    <Typography fontSize={13}>
                        Status: <strong>{getBankStatusLabel(currentStatus)}</strong>
                    </Typography>

                    {canApprove && (
                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <Select
                                value={documentType}
                                onChange={(e) => setDocumentType(e.target.value)}
                            >
                                <MenuItem value={DOCUMENT_TYPE.CHEQUE}>Cheque</MenuItem>
                                <MenuItem value={DOCUMENT_TYPE.PASSBOOK}>First Page of Passbook</MenuItem>
                                <MenuItem value={DOCUMENT_TYPE.STATEMENT}>Statement</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </Card>

                {!isApproved && (
                    <Card>
                        <Typography variant="subtitle2" fontWeight={600} mb={1}>
                            Take Action
                        </Typography>

                        <Button
                            variant="contained"
                            fullWidth
                            sx={{ bgcolor: "#2e7d32", mb: 1 }}
                            onClick={handleApprove}
                            disabled={isPending || !canApprove}
                        >
                            Approve
                        </Button>

                        <Button
                            variant="contained"
                            fullWidth
                            sx={{ bgcolor: "#d32f2f", "&:hover": { bgcolor: "#c62828" }, mb: 1 }}
                            onClick={handleReject}
                            disabled={isPending || isRejected}
                        >
                            Reject
                        </Button>

                        <Button
                            variant="contained"
                            fullWidth
                            sx={{ bgcolor: "#rgb(176, 101, 39)", "&:hover": { bgcolor: "rgb(176, 101, 39)" } }}
                            onClick={handleOnHold}
                            disabled={isPending || isOnHold || isRejected}
                        >
                            On Hold
                        </Button>

                        {isPending && (
                            <Box display="flex" justifyContent="center" mt={1}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                    </Card>
                )}
            </Box>
        </RootStyle>
    );
}